<script>
import { defineAsyncComponent } from 'vue';
const ToolsPopup = defineAsyncComponent(() =>
  import(/* webpackChunkName: "toolsPopup" */ './ToolsPopup.vue')
);
const UserPopup = defineAsyncComponent(() =>
  import(/* webpackChunkName: "userPopup" */ './UserPopup.vue')
);

export default {
  components: {
    ToolsPopup,
    UserPopup,
  },
  props: [
    'logo',
    'toolsActive',
    'userActive',
    'loginActive',
    'tools',
    'user',
    'userButton',
    'searchButton',
    'newsNotification',
    'toolsButton',
    'loginButton',
    'externalLoginButton',
    'menuButton',
    'isLoggedIn',
    'addToolLinktext',
    'addToolLinkurl',
    'addToolLinkicon',
    'addToolLinkclass',
    'staticSearchUrl',
    'showMenuState',
    'colorTheme',
  ],
  data() {
    return {
      isDarkMode: false,
      showingSearch: false,
    };
  },
  mounted() {
    document.addEventListener(
      'keydown',
      (event) => this.setFocusInMenu(event),
      {
        capture: true,
      }
    );
    if (document.documentElement.classList.contains('main-site-only')) {
      // darkmode is available
      if (
        (window.matchMedia &&
          window.matchMedia('(prefers-color-scheme: dark)').matches) ||
        document.documentElement.getAttribute('data-color-scheme') === 'dark'
      ) {
        // Has selected or prefers dark mode
        if (this.logo.logoDarkModeImageUrl) {
          // Dark mode logo exists
          this.isDarkMode = true;
        } else {
          this.isDarkMode = false;
        }
      } else {
        this.isDarkMode = false;
      }
    }
  },
  watch: {
    colorTheme(newTheme) {
      if (
        newTheme === 'dark' ||
        (newTheme === 'system' &&
          window.matchMedia &&
          window.matchMedia('(prefers-color-scheme: dark)').matches)
      ) {
        if (this.logo.logoDarkModeImageUrl) {
          this.isDarkMode = true;
        } else {
          this.isDarkMode = false;
        }
      } else {
        this.isDarkMode = false;
      }
    },
  },
  beforeUnmount() {
    document.removeEventListener(
      'keydown',
      (event) => this.setFocusInMenu(event),
      {
        capture: true,
      }
    );
  },
  methods: {
    showMenu() {
      this.$emit('show-menu');
    },
    showSearch() {
      this.$emit('show-search');
    },
    showTools() {
      this.$emit('show-tools');
    },
    showUser() {
      this.$emit('show-user');
    },
    showLogin() {
      this.$emit('show-login');
    },
    toggleDropDownSearch() {
      this.showingSearch = !this.showingSearch;
      const foldOutSearch = document.querySelector('.fold-out-search');
      const foldOutSearchContent = document.querySelector('.fold-out-search__content');

      if (this.showingSearch) {
        document.body.classList.add('search-active');

        setTimeout(() => {
          document.body.classList.add('search-active--visible');
          if (foldOutSearch) {
            window.scrollTo({
              top: 0,
              behavior: 'smooth',
            });

            const input = foldOutSearch.querySelector('input.gsc-input');

            if (input) {
              input.focus();
            }
          }
        }, 500);

        // Send event
        if (_paq) {
          _paq.push(['trackEvent', 'Interaktion med sökknapp', 'Öppna', 'Sökfältet visas']);
        }
      } else {
        document.body.classList.remove('search-active');
        document.body.classList.remove('search-active--visible');

        // Try and clean the search input
        // const clearBtn = document.querySelector('a.gsst_a');
        // if (clearBtn) {
        //   clearBtn.click();
        // }
      }
    },
    setFocusInMenu(event) {
      // When user presses tab whilst the main menu button is focused,
      // focus the first element in the menu
      const menuWrapper = document.querySelector('.menu-popup__wrapper');

      if (
        event.key === 'Tab' &&
        !event.shiftKey &&
        document.activeElement?.id === 'main-menu-button' &&
        menuWrapper
      ) {
        event.preventDefault();
        event.stopPropagation();

        const firstListElement = menuWrapper.querySelector('button, a');
        if (firstListElement) {
          firstListElement.focus();
        }
      }
    },
  },
};
</script>

<template>
  <div class="miun-menu__mainmenu-wrapper">
    <div class="container">
      <div class="row">
        <div class="col">
          <div class="miun-menu">
            <div class="miun-menu__menu">
              <button
                id="main-menu-button"
                class="m-link m-menu no-style"
                :aria-expanded="showMenuState"
                @click="showMenu">
                <i
                  v-if="showMenuState"
                  class="material-icons menu"
                  aria-hidden="true"
                  >close</i
                >
                <i v-else class="material-icons menu" aria-hidden="true"
                  >menu</i
                >
                <span>{{ menuButton.text }}</span>
              </button>
              <div class="miun-menu__tools d-block d-md-none" v-if="isLoggedIn">
                <button
                  class="m-link miun-menu__tools__button"
                  @click="showTools">
                  <i class="material-icons build-icon" aria-hidden="true">{{
                    toolsButton.icon
                  }}</i
                  ><span>{{ toolsButton.text }}</span>
                </button>
                <!-- <transition name="fade" mode="out-in"> -->
                <ToolsPopup
                  :addToolLinktext="addToolLinktext"
                  :addToolLinkurl="addToolLinkurl"
                  :addToolLinkicon="addToolLinkicon"
                  :addToolLinkclass="addToolLinkclass"
                  :tools="tools"
                  @hide-tools="showTools"
                  v-show="toolsActive"
                  :isActive="toolsActive"></ToolsPopup>
                <!-- </transition> -->
              </div>
            </div>
            <div class="miun-menu__logo">
              <a
                :href="logo.logoLinkUrl"
                :title="logo.logoAltText"
                :aria-label="logo.logoAltText">
                <img
                  :src="
                    isDarkMode ? logo.logoDarkModeImageUrl : logo.logoImageUrl
                  "
                  :alt="logo.logoAltText"
                  :width="logo.logoWidth"
                  :height="logo.logoHeight" />
              </a>
            </div>

            <nav class="miun-menu__nav">
              <!-- <a
                v-if="staticSearchUrl"
                :href="staticSearchUrl"
                class="d-none d-md-flex m-link m-search"
                :aria-label="searchButton.text">
                <i class="material-icons search">{{ searchButton.icon }}</i>
                <span>{{ searchButton.text }}</span>
              </a> -->
              <div class="miun-menu__tools d-none d-md-block" v-if="isLoggedIn">
                <button
                  class="m-link miun-menu__tools__button"
                  @click="showTools">
                  <i class="material-icons build-icon" aria-hidden="true">{{
                    toolsButton.icon
                  }}</i
                  ><span>{{ toolsButton.text }}</span>
                </button>
                <!-- <transition name="fade" mode="out-in"> -->
                <ToolsPopup
                  :addToolLinktext="addToolLinktext"
                  :addToolLinkurl="addToolLinkurl"
                  :addToolLinkicon="addToolLinkicon"
                  :addToolLinkclass="addToolLinkclass"
                  :tools="tools"
                  @hide-tools="showTools"
                  v-show="toolsActive"
                  :isActive="toolsActive"></ToolsPopup>
                <!-- </transition> -->
              </div>
              <div class="miun-menu__user d-flex" v-if="isLoggedIn">
                <!-- <transition name="fade" mode="out-in"> -->
                <template v-if="userActive">
                  <UserPopup
                    :user="user"
                    @hide-user="showUser"
                    :isActive="userActive"></UserPopup>
                </template>
                <!-- </transition> -->
                <button
                  :aria-label="userButton.name"
                  @click="showUser"
                  class="user-button"
                  :class="userButton.color">
                  <span>{{ userButton.initials }}</span>
                  <div v-if="newsNotification.text" class="avatar-notification">
                    {{ newsNotification.text }}
                  </div>
                </button>
              </div>

              <div class="miun-menu__user" v-if="!isLoggedIn">
                <a :href="loginButton.linkUrl" class="m-link m-login no-style">
                  <i class="material-icons" aria-hidden="true">
                    {{ loginButton.linkIcon }}
                  </i>
                  <span>
                    {{ loginButton.linkText }}
                  </span>
                </a>
              </div>
              <button
                class="m-link m-search m-search--fold-out"
                :aria-expanded="showingSearch"
                @click="toggleDropDownSearch">
                <i class="material-icons search fold-out-icon-search">{{
                  searchButton.icon
                }}</i>
                <i class="material-icons close fold-out-icon-close">close</i>
                <span>{{ searchButton.text }}</span>
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
